import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import ErrorBoundary from "./components/common/ErrorBoundary";
import LoadingSpinner from "./components/common/LoadingSpinner";
import Dashboard from "./pages/Dashboard";
import TopUp from "./pages/TopUp";
import Usage from "./pages/Usage";
import Tariff from "./pages/Tariff";
import Payments from "./pages/Payments";
import Endpoints from "./pages/Endpoints";
import EndpointDetail from "./pages/EndpointDetail";
import Settings from "./pages/Settings";
import Onboarding from "./pages/Onboarding";
import ChatWidget from "./components/common/ChatWidget";
import { signupApi } from "./api/signup";
import { useAuth } from "./hooks/useAuth";

type AppStatus =
  | "loading"
  | "unregistered"
  | "pending_setup"
  | "pending_topup"
  | "active"
  | "error";

export default function App() {
  const { user } = useAuth();
  const [appStatus, setAppStatus] = useState<AppStatus>("loading");
  const [statusEmail, setStatusEmail] = useState<string | undefined>();

  const checkStatus = useCallback(async () => {
    setAppStatus("loading");
    try {
      const result = await signupApi.getStatus();
      const data = result.data;
      setStatusEmail(data?.email ?? user?.email);

      if (data?.role === "admin" || data?.status === "active") {
        setAppStatus("active");
      } else if (data?.status === "pending_topup") {
        // Customer has token, let them into the full portal (they can top up)
        setAppStatus("active");
      } else if (data?.status === "pending_setup") {
        setAppStatus("pending_setup");
      } else {
        setAppStatus("unregistered");
      }
    } catch {
      // If auth fails or API unreachable, show error
      setAppStatus("error");
    }
  }, [user?.email]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // ── Loading state ──────────────────────────────────────────

  if (appStatus === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────

  if (appStatus === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.27 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-lg font-bold text-gray-900">
            Something went wrong
          </h1>
          <p className="mb-4 text-sm text-gray-600">
            Unable to load your account. Please check your connection and try
            again.
          </p>
          <button
            onClick={checkStatus}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Onboarding (unregistered or pending_setup) ─────────────

  if (appStatus === "unregistered" || appStatus === "pending_setup") {
    return (
      <ErrorBoundary>
        <Onboarding
          status={appStatus}
          email={statusEmail}
          onComplete={checkStatus}
        />
      </ErrorBoundary>
    );
  }

  // ── Full application ───────────────────────────────────────

  return (
    <ErrorBoundary>
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-y-auto p-6">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/topup" element={<TopUp />} />
              <Route path="/usage" element={<Usage />} />
              <Route path="/tariff" element={<Tariff />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/endpoints" element={<Endpoints />} />
              <Route path="/endpoints/:id" element={<EndpointDetail />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </main>
        </div>
      </div>
      <ChatWidget />
    </ErrorBoundary>
  );
}
